/*
  Jaiman Toys – Wishlist v2 (theme-native, no app)
  ------------------------------------------------
  • Zero network requests on normal page loads (saved items live in the browser).
  • Guest wishlist + separate wishlist per logged-in customer on the same device.
    When a guest logs in, their guest items are moved into their account list.
  • Slide-out wishlist panel, toasts with Undo, header / bottom-nav counters.
  • Live price + stock refresh only when the panel / page is opened (cached 10 min).
  • Price-drop and back-in-stock badges, Add to cart / Add all, Share (WhatsApp / link).
  • Optional cross-device sync for logged-in customers via an app proxy (JTW.syncUrl).
  Config comes from snippets/jt-wishlist-drawer.liquid (window.JTW).
*/
(() => {
  if (window.__jtWishlist) return;
  window.__jtWishlist = true;

  const CFG = Object.assign(
    {
      customer: null,
      loginUrl: '/account/login',
      registerUrl: '/account/register',
      pageUrl: '/pages/wishlist',
      shopUrl: '/collections/all',
      moneyFormat: 'Rs. {{amount}}',
      shopName: 'Jaiman Toys',
      syncUrl: '',
      max: 100,
    },
    window.JTW || {}
  );
  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const GUEST = 'jtw_guest';
  const OLD = 'jt_wishlist_v1';
  const KEY = CFG.customer ? 'jtw_c_' + CFG.customer.id : GUEST;
  const LIVE = 'jtw_live';
  const TTL = 10 * 60 * 1000;

  // ---------------------------------------------------------------- storage
  const store = (s) => ({
    get(k) { try { return JSON.parse(s.getItem(k)); } catch (e) { return null; } },
    set(k, v) { try { s.setItem(k, JSON.stringify(v)); } catch (e) {} },
    del(k) { try { s.removeItem(k); } catch (e) {} },
  });
  let ls, ss;
  try { ls = store(window.localStorage); ss = store(window.sessionStorage); }
  catch (e) { const m = new Map(); const mem = { getItem: (k) => m.get(k) ?? null, setItem: (k, v) => m.set(k, v), removeItem: (k) => m.delete(k) }; ls = store(mem); ss = store(mem); }

  // Same product AND (same variant, or either side saved without a variant)
  const matches = (i, h, v) => i.h === h && (!v || !i.v || String(i.v) === String(v));
  const keyOf = (i) => i.h + '|' + (i.v || '');

  let pendingNotice = '';
  let list = load();

  function load() {
    let l = ls.get(KEY);
    if (!Array.isArray(l)) l = [];
    const old = ls.get(OLD); // v1 format {h, v, t}
    if (Array.isArray(old) && old.length) {
      old.forEach((o) => { if (o && o.h && !l.some((i) => matches(i, o.h, o.v))) l.push({ h: o.h, v: o.v || '', t: o.t || Date.now() }); });
      ls.del(OLD);
    }
    if (CFG.customer) {
      const g = ls.get(GUEST);
      if (Array.isArray(g) && g.length) {
        let n = 0;
        g.forEach((o) => { if (o && o.h && !l.some((i) => matches(i, o.h, o.v))) { l.push(o); n++; } });
        ls.del(GUEST);
        if (n) pendingNotice = n + (n > 1 ? ' items' : ' item') + ' from this device moved to your account wishlist';
      }
    }
    l = l.filter((i) => i && i.h).slice(0, CFG.max);
    ls.set(KEY, l);
    return l;
  }

  function commit(opts = {}) {
    list = list.slice(0, CFG.max);
    ls.set(KEY, list);
    paint();
    renderAll();
    if (!opts.silent) queueSync();
    document.dispatchEvent(new CustomEvent('jt:wishlist:change', { detail: { count: list.length } }));
  }

  // ---------------------------------------------------------------- helpers
  const $ = (s, el = document) => el.querySelector(s);
  const $$ = (s, el = document) => [...el.querySelectorAll(s)];
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const sized = (src, w) => {
    if (!src) return '';
    if (src.startsWith('data:')) return src;
    if (src.startsWith('//')) src = 'https:' + src;
    return src.replace(/([?&])width=\d+&?/, '$1').replace(/[?&]$/, '') + (src.includes('?') ? '&' : '?') + 'width=' + w;
  };
  function money(cents) {
    cents = Math.round(Number(cents) || 0);
    const grp = (dec, th, ds) => {
      const p = (cents / 100).toFixed(dec).split('.');
      p[0] = p[0].replace(/\B(?=(\d{3})+(?!\d))/g, th);
      return p.join(ds);
    };
    return String(CFG.moneyFormat || 'Rs. {{amount}}')
      .replace(/<[^>]*>/g, '')
      .replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => {
        switch (k) {
          case 'amount_no_decimals': return grp(0, ',', '.');
          case 'amount_with_comma_separator': return grp(2, '.', ',');
          case 'amount_no_decimals_with_comma_separator': return grp(0, '.', ',');
          case 'amount_with_apostrophe_separator': return grp(2, "'", '.');
          default: return grp(2, ',', '.');
        }
      });
  }
  const pct = (p, c) => (c > p ? Math.round(((c - p) / c) * 100) : 0);
  const HEART = '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false"><path d="M12 21s-7.5-4.6-9.6-9.2C.9 8.4 3 4.5 6.7 4.5c2.1 0 3.6 1.1 5.3 3 1.7-1.9 3.2-3 5.3-3 3.7 0 5.8 3.9 4.3 7.3C19.5 16.4 12 21 12 21z"/></svg>';
  const TRASH = '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M4 7h16M9 7V4.5h6V7M6.5 7l1 13h9l1-13M10 11v6M14 11v6"/></svg>';
  const CLOSE = '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>';
  const WA = '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="currentColor"><path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2zm0 18.2a8.2 8.2 0 0 1-4.2-1.2l-.3-.2-3 .8.8-2.9-.2-.3A8.2 8.2 0 1 1 12 20.2zm4.5-6.1c-.2-.1-1.5-.7-1.7-.8-.2-.1-.4-.1-.6.1l-.8 1c-.1.2-.3.2-.5.1a6.7 6.7 0 0 1-3.3-2.9c-.3-.4.3-.4.8-1.3.1-.2 0-.3 0-.4l-.8-1.8c-.2-.5-.4-.4-.6-.4h-.5a1 1 0 0 0-.7.3 3 3 0 0 0-.9 2.2 5.2 5.2 0 0 0 1.1 2.7 11.8 11.8 0 0 0 4.5 4c1.7.7 2.3.8 3.2.6.5-.1 1.5-.6 1.7-1.2.2-.6.2-1.1.2-1.2-.1-.1-.3-.2-.5-.3z"/></svg>';
  const LINK = '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M10 14a4.5 4.5 0 0 0 6.4 0l3-3a4.5 4.5 0 0 0-6.4-6.4l-1 1M14 10a4.5 4.5 0 0 0-6.4 0l-3 3a4.5 4.5 0 0 0 6.4 6.4l1-1"/></svg>';

  // ---------------------------------------------------------------- product-page variant
  function variantFor(btn) {
    if (!btn.hasAttribute('data-use-form')) return '';
    const scope = btn.closest('product-info, [id^="MainProduct-"], .product') || document;
    const input =
      scope.querySelector('form[id^="product-form-"]:not(.installment) [name="id"]') ||
      scope.querySelector('form[action*="/cart/add"] [name="id"]') ||
      document.querySelector('form[action*="/cart/add"] [name="id"]');
    return input && input.value ? input.value : '';
  }

  // Snapshot of what the shopper saw when saving (so the panel renders instantly)
  function snapshot(btn, v) {
    const d = btn.dataset;
    const s = {
      title: d.title || '', url: d.url || '', img: d.img || '',
      price: +d.price || 0, cmp: +d.cmp || 0, avail: d.avail === 'true',
      vid: v || d.vid || '', multi: d.multi === 'true', vt: '',
    };
    if (v && d.variants) {
      try {
        const m = JSON.parse(d.variants)[v];
        if (m) {
          s.price = m[0]; s.cmp = m[1]; s.avail = !!m[2];
          s.vt = m[3] && m[3] !== 'Default Title' ? m[3] : '';
          if (m[4]) s.img = m[4];
        }
      } catch (e) {}
    }
    return s;
  }

  // ---------------------------------------------------------------- hearts + counters
  function paint() {
    $$('[data-jt-wish]').forEach((btn) => {
      const on = list.some((i) => matches(i, btn.dataset.handle, variantFor(btn)));
      if (btn.classList.contains('is-active') !== on) btn.classList.toggle('is-active', on);
      const pressed = on ? 'true' : 'false';
      if (btn.getAttribute('aria-pressed') !== pressed) {
        btn.setAttribute('aria-pressed', pressed);
        btn.setAttribute('title', on ? 'Remove from wishlist' : 'Add to wishlist');
      }
    });
    const n = String(list.length);
    $$('[data-jt-wish-count]').forEach((el) => {
      if (el.textContent !== n) {
        el.textContent = n;
        el.classList.remove('jtw-bump'); void el.offsetWidth; el.classList.add('jtw-bump');
      }
      el.hidden = !list.length;
    });
  }

  function pop(btn) { btn.classList.remove('jt-pop'); void btn.offsetWidth; btn.classList.add('jt-pop'); }

  function toggle(btn) {
    const h = btn.dataset.handle;
    if (!h) return;
    const v = variantFor(btn);
    const existing = list.filter((i) => matches(i, h, v));
    if (existing.length) {
      list = list.filter((i) => !matches(i, h, v));
      commit();
      toast({ img: existing[0].img, title: 'Removed from wishlist', action: 'Undo', onAction: () => { list = existing.concat(list); commit(); } });
    } else {
      const s = snapshot(btn, v);
      list.unshift(Object.assign({ h, v, t: Date.now(), at: s.price, out: !s.avail }, s));
      commit();
      toast({ img: s.img, title: 'Saved to wishlist', text: s.title, action: 'View', onAction: openDrawer });
    }
    pop(btn);
  }

  // ---------------------------------------------------------------- live refresh (panel/page only)
  const slim = (p) => ({
    title: p.title, url: p.url || '/products/' + p.handle, img: p.featured_image || '',
    vs: (p.variants || []).map((x) => [x.id, x.price, x.compare_at_price || 0, x.available, x.title, x.featured_image ? x.featured_image.src : '']),
  });
  async function pool(items, n, fn) {
    const q = items.slice();
    await Promise.all(Array.from({ length: Math.min(n, q.length) }, async () => { while (q.length) await fn(q.shift()); }));
  }
  async function fetchProducts(handles) {
    const cache = ss.get(LIVE) || {};
    const now = Date.now();
    const need = [...new Set(handles)].filter((h) => !(cache[h] && now - cache[h].t < TTL));
    await pool(need, 6, async (h) => {
      try {
        const r = await fetch(root + 'products/' + encodeURIComponent(h) + '.js', { headers: { Accept: 'application/json' } });
        if (r.status === 404) { cache[h] = { t: now, gone: 1 }; return; }
        if (!r.ok) return;
        cache[h] = { t: now, p: slim(await r.json()) };
      } catch (e) {}
    });
    ss.set(LIVE, cache);
    return cache;
  }
  function applyLive(i, c) {
    const p = c.p;
    const chosen = i.v ? p.vs.find((x) => String(x[0]) === String(i.v)) : null;
    if (i.v && !chosen) i.v = ''; // variant was deleted
    const vv = chosen || p.vs.find((x) => x[3]) || p.vs[0];
    if (!vv) return;
    i.title = p.title; i.url = p.url;
    i.multi = p.vs.length > 1;
    i.img = (chosen && chosen[5]) || p.img || i.img;
    i.vt = chosen && i.multi && vv[4] !== 'Default Title' ? vv[4] : '';
    i.vid = vv[0]; i.price = vv[1]; i.cmp = vv[2];
    const was = i.avail;
    i.avail = !!vv[3];
    if (i.out && i.avail) i.back = true; // was sold out when saved, now in stock
    if (was === false && i.avail) i.back = true;
    if (i.at == null) i.at = i.price;
  }
  let refreshing = null;
  function refresh() {
    if (!list.length) return Promise.resolve();
    if (refreshing) return refreshing;
    refreshing = fetchProducts(list.map((i) => i.h)).then((cache) => {
      const before = list.length;
      list = list.filter((i) => !(cache[i.h] && cache[i.h].gone));
      list.forEach((i) => { if (cache[i.h] && cache[i.h].p) applyLive(i, cache[i.h]); });
      ls.set(KEY, list);
      if (before !== list.length) paint();
      renderAll();
    }).finally(() => { refreshing = null; });
    return refreshing;
  }

  // ---------------------------------------------------------------- item markup
  function badges(i) {
    const out = [];
    if (i.at && i.price && i.price < i.at) out.push(`<span class="jtw-badge jtw-badge--drop">Price dropped ${esc(money(i.at - i.price))}</span>`);
    if (i.back && i.avail) out.push('<span class="jtw-badge jtw-badge--back">Back in stock</span>');
    if (i.avail === false) out.push('<span class="jtw-badge jtw-badge--out">Sold out</span>');
    return out.length ? `<div class="jtw-badges">${out.join('')}</div>` : '';
  }
  function itemHTML(i, opts = {}) {
    const url = (i.url || root + 'products/' + i.h) + (i.v ? '?variant=' + i.v : '');
    const loading = !i.title;
    const off = pct(i.price, i.cmp);
    const needsOptions = i.multi && !i.v;
    let cta;
    if (opts.shared) cta = `<button type="button" class="jtw-btn jtw-btn--ghost" data-jtw-save="${esc(i.h)}" data-v="${esc(i.v || '')}">${HEART} Save</button>`;
    else if (i.avail === false) cta = '<button type="button" class="jtw-btn" disabled>Sold out</button>';
    else if (needsOptions) cta = `<a class="jtw-btn jtw-btn--outline" href="${esc(url)}">Choose options</a>`;
    else cta = `<button type="button" class="jtw-btn" data-jtw-atc="${esc(i.vid || i.v || '')}">Add to cart</button>`;
    const atc2 = opts.shared && i.avail !== false && !needsOptions ? `<button type="button" class="jtw-btn" data-jtw-atc="${esc(i.vid || i.v || '')}">Add to cart</button>` : '';
    return `
      <li class="jtw-item${loading ? ' is-loading' : ''}${i.avail === false ? ' is-out' : ''}" data-h="${esc(i.h)}" data-v="${esc(i.v || '')}">
        <a class="jtw-item__img" href="${esc(url)}" tabindex="-1">
          ${i.img ? `<img src="${esc(sized(i.img, 300))}" alt="${esc(i.title)}" loading="lazy" width="300" height="300">` : '<span class="jtw-skel"></span>'}
          ${off ? `<span class="jtw-off">-${off}%</span>` : ''}
        </a>
        <div class="jtw-item__body">
          <a class="jtw-item__title" href="${esc(url)}">${loading ? '<span class="jtw-skel jtw-skel--line"></span>' : esc(i.title)}</a>
          ${i.vt ? `<div class="jtw-item__vt">${esc(i.vt)}</div>` : ''}
          ${i.price ? `<div class="jtw-item__price"><strong>${esc(money(i.price))}</strong>${i.cmp > i.price ? `<s>${esc(money(i.cmp))}</s>` : ''}</div>` : ''}
          ${opts.shared ? '' : badges(i)}
          <div class="jtw-item__actions">
            ${cta}${atc2}
            ${opts.shared ? '' : `<button type="button" class="jtw-icon-btn" data-jtw-rm aria-label="Remove ${esc(i.title || 'item')} from wishlist" title="Remove">${TRASH}</button>`}
          </div>
        </div>
      </li>`;
  }
  function emptyHTML() {
    return `
      <div class="jtw-empty">
        <div class="jtw-empty__art">${HEART}</div>
        <h3>Your wishlist is empty</h3>
        <p>Tap the <span class="jtw-inline-heart">${HEART}</span> on any toy, model or collectible to save it here.</p>
        <a class="jtw-btn" href="${esc(CFG.shopUrl)}">Start exploring</a>
      </div>`;
  }
  function accountHTML() {
    if (CFG.customer) {
      const name = CFG.customer.name ? 'Hi ' + esc(CFG.customer.name) + '! ' : '';
      return `<div class="jtw-acct jtw-acct--in"><span class="jtw-acct__dot"></span>${name}${CFG.syncUrl ? 'Your wishlist is synced to your account.' : 'Your wishlist is saved to your account on this device.'}</div>`;
    }
    const ret = encodeURIComponent(location.pathname + location.search);
    return `<div class="jtw-acct"><span>🔒 Sign in to keep your wishlist safe in your account.</span><a href="${esc(CFG.loginUrl + (CFG.loginUrl.includes('?') ? '&' : '?') + 'return_url=' + ret)}" class="jtw-link">Sign in</a></div>`;
  }
  function inStock() { return list.filter((i) => i.avail !== false && !(i.multi && !i.v) && (i.vid || i.v)); }
  function shareUrl() {
    const ids = list.slice(0, 40).map((i) => (i.v ? i.h + ':' + i.v : i.h)).join(',');
    return location.origin + CFG.pageUrl + '?share=' + encodeURIComponent(ids);
  }

  // ---------------------------------------------------------------- drawer
  let drawer, lastFocus;
  function buildDrawer() {
    if (drawer) return drawer;
    drawer = document.createElement('div');
    drawer.className = 'jtw-root';
    drawer.innerHTML = `
      <div class="jtw-overlay" data-jtw-close></div>
      <aside class="jtw-drawer" role="dialog" aria-modal="true" aria-labelledby="JtwTitle" tabindex="-1">
        <header class="jtw-head">
          <div class="jtw-head__title"><span class="jtw-head__icon">${HEART}</span><h2 id="JtwTitle">My Wishlist</h2><span class="jtw-chip" data-jtw-count></span></div>
          <button type="button" class="jtw-icon-btn jtw-x" data-jtw-close aria-label="Close wishlist">${CLOSE}</button>
        </header>
        <div data-jtw-acct></div>
        <div class="jtw-body"><ul class="jtw-list" data-jtw-list></ul></div>
        <footer class="jtw-foot" data-jtw-foot></footer>
      </aside>`;
    document.body.appendChild(drawer);
    document.addEventListener('keydown', (e) => {
      if (!drawer.classList.contains('is-open')) return;
      if (e.key === 'Escape') closeDrawer();
      if (e.key === 'Tab') { // keep focus inside
        const f = $$('a[href],button:not([disabled]),[tabindex="0"]', drawer.querySelector('.jtw-drawer')).filter((x) => x.offsetParent);
        if (!f.length) return;
        if (e.shiftKey && document.activeElement === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
        else if (!e.shiftKey && document.activeElement === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
      }
    });
    return drawer;
  }
  function renderDrawer() {
    if (!drawer || !drawer.classList.contains('is-open')) return;
    $('[data-jtw-acct]', drawer).innerHTML = accountHTML();
    const c = $('[data-jtw-count]', drawer);
    c.textContent = list.length ? list.length + (list.length > 1 ? ' items' : ' item') : '';
    const ul = $('[data-jtw-list]', drawer);
    ul.innerHTML = list.length ? list.map((i) => itemHTML(i)).join('') : emptyHTML();
    const foot = $('[data-jtw-foot]', drawer);
    if (!list.length) { foot.innerHTML = ''; foot.hidden = true; return; }
    foot.hidden = false;
    const ok = inStock();
    const total = ok.reduce((s, i) => s + (i.price || 0), 0);
    foot.innerHTML = `
      <div class="jtw-total"><span>Total${ok.length !== list.length ? ' (' + ok.length + ' available)' : ''}</span><strong>${esc(money(total))}</strong></div>
      <button type="button" class="jtw-btn jtw-btn--block" data-jtw-add-all ${ok.length ? '' : 'disabled'}>Add ${ok.length > 1 ? 'all ' + ok.length + ' ' : ''}to cart</button>
      <div class="jtw-share">
        <span>Share:</span>
        <button type="button" class="jtw-pill jtw-pill--wa" data-jtw-wa>${WA} WhatsApp</button>
        <button type="button" class="jtw-pill" data-jtw-copy>${LINK} Copy link</button>
        <a class="jtw-link jtw-share__all" href="${esc(CFG.pageUrl)}">View full page →</a>
      </div>`;
  }
  function openDrawer() {
    buildDrawer();
    lastFocus = document.activeElement;
    drawer.classList.add('is-open');
    document.documentElement.classList.add('jtw-lock');
    renderDrawer();
    requestAnimationFrame(() => { const x = $('.jtw-x', drawer); x && x.focus({ preventScroll: true }); });
    refresh();
    pull();
  }
  function closeDrawer() {
    if (!drawer || !drawer.classList.contains('is-open')) return;
    drawer.classList.remove('is-open');
    document.documentElement.classList.remove('jtw-lock');
    if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
  }

  // ---------------------------------------------------------------- full page (/pages/wishlist)
  let pageState = { sort: 'recent', stock: false };
  let shared = null; // items from ?share=
  function sorted(items) {
    const a = items.slice();
    if (pageState.stock) return sortBy(a.filter((i) => i.avail !== false));
    return sortBy(a);
  }
  function sortBy(a) {
    switch (pageState.sort) {
      case 'low': return a.sort((x, y) => (x.price || 0) - (y.price || 0));
      case 'high': return a.sort((x, y) => (y.price || 0) - (x.price || 0));
      case 'drop': return a.sort((x, y) => ((y.at || 0) - (y.price || 0)) - ((x.at || 0) - (x.price || 0)));
      default: return a.sort((x, y) => (y.t || 0) - (x.t || 0));
    }
  }
  function renderPage() {
    const el = $('[data-jt-wishlist-page]');
    if (!el) return;
    if (shared) return renderShared(el);
    if (!list.length) { el.innerHTML = `${el.dataset.login !== 'false' ? accountHTML() : ''}${emptyHTML()}`; return; }
    const ok = inStock();
    const drops = list.filter((i) => i.at && i.price && i.price < i.at).length;
    const items = sorted(list);
    el.innerHTML = `
      ${el.dataset.login !== 'false' ? accountHTML() : ''}
      <div class="jtw-toolbar">
        <div class="jtw-toolbar__info"><strong>${list.length}</strong> saved ${list.length > 1 ? 'items' : 'item'}${drops ? ` · <span class="jtw-drop-note">${drops} price drop${drops > 1 ? 's' : ''} 🎉</span>` : ''}</div>
        <div class="jtw-toolbar__controls">
          <label class="jtw-select"><span class="visually-hidden">Sort</span>
            <select data-jtw-sort>
              <option value="recent"${pageState.sort === 'recent' ? ' selected' : ''}>Recently saved</option>
              <option value="low"${pageState.sort === 'low' ? ' selected' : ''}>Price: low to high</option>
              <option value="high"${pageState.sort === 'high' ? ' selected' : ''}>Price: high to low</option>
              <option value="drop"${pageState.sort === 'drop' ? ' selected' : ''}>Biggest price drop</option>
            </select>
          </label>
          <label class="jtw-check"><input type="checkbox" data-jtw-stock${pageState.stock ? ' checked' : ''}> In stock only</label>
        </div>
      </div>
      <ul class="jtw-list jtw-list--grid">${items.map((i) => itemHTML(i)).join('') || '<li class="jtw-none">No items in stock right now.</li>'}</ul>
      <div class="jtw-pagebar">
        <button type="button" class="jtw-btn" data-jtw-add-all ${ok.length ? '' : 'disabled'}>Add ${ok.length > 1 ? 'all ' + ok.length + ' ' : ''}to cart · ${esc(money(ok.reduce((s, i) => s + (i.price || 0), 0)))}</button>
        ${el.dataset.share !== 'false' ? `<button type="button" class="jtw-pill jtw-pill--wa" data-jtw-wa>${WA} Share on WhatsApp</button><button type="button" class="jtw-pill" data-jtw-copy>${LINK} Copy link</button>` : ''}
        <button type="button" class="jtw-link jtw-clear" data-jtw-clear>Clear wishlist</button>
      </div>`;
  }
  function renderShared(el) {
    const items = shared.items;
    el.innerHTML = `
      <div class="jtw-shared-head">
        <span class="jtw-head__icon">${HEART}</span>
        <div><h2>A wishlist was shared with you</h2><p>${items.length} ${items.length > 1 ? 'picks' : 'pick'} from ${esc(CFG.shopName)}</p></div>
        <div class="jtw-shared-head__actions">
          <button type="button" class="jtw-btn" data-jtw-save-all>${HEART} Save all to my wishlist</button>
          <a class="jtw-link" href="${esc(CFG.pageUrl)}">View my wishlist</a>
        </div>
      </div>
      <ul class="jtw-list jtw-list--grid">${items.map((i) => itemHTML(i, { shared: true })).join('')}</ul>`;
  }
  async function loadShared(param) {
    const parts = param.split(',').map((s) => s.trim()).filter(Boolean).slice(0, 40);
    const items = parts.map((s) => { const [h, v] = s.split(':'); return { h, v: v || '', t: Date.now() }; });
    shared = { items };
    renderPage();
    const cache = await fetchProducts(items.map((i) => i.h));
    shared.items = items.filter((i) => cache[i.h] && cache[i.h].p);
    shared.items.forEach((i) => applyLive(i, cache[i.h]));
    renderPage();
  }

  function renderAll() { renderDrawer(); renderPage(); }

  // ---------------------------------------------------------------- cart
  function updateCartUI(sections) {
    if (sections && sections['cart-icon-bubble']) {
      const doc = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
      const src = doc.querySelector('.shopify-section') || doc.body;
      const dest = document.getElementById('cart-icon-bubble');
      if (dest && src) dest.innerHTML = src.innerHTML;
    }
    fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((c) => {
        const b = document.getElementById('MobileBottomNav-CartCount');
        if (b) { b.textContent = c.item_count; b.hidden = !c.item_count; }
      }).catch(() => {});
  }
  async function addToCart(ids, btn) {
    ids = ids.filter(Boolean);
    if (!ids.length) return;
    const label = btn.innerHTML;
    btn.disabled = true;
    btn.classList.add('is-busy');
    btn.textContent = 'Adding…';
    const cartDrawer = document.querySelector('cart-drawer');
    const useDrawer = !!(cartDrawer && typeof cartDrawer.renderContents === 'function');
    const body = { items: ids.map((id) => ({ id: Number(id), quantity: 1 })), sections: useDrawer ? 'cart-drawer,cart-icon-bubble' : 'cart-icon-bubble', sections_url: location.pathname };
    try {
      const r = await fetch(root + 'cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
      const j = await r.json();
      if (!r.ok || j.status) throw new Error(j.description || j.message || 'Could not add to cart');
      if (window.publish && window.PUB_SUB_EVENTS && PUB_SUB_EVENTS.cartUpdate) {
        try { publish(PUB_SUB_EVENTS.cartUpdate, { source: 'jt-wishlist', cartData: j }); } catch (e) {}
      }
      btn.classList.remove('is-busy');
      btn.classList.add('is-done');
      btn.textContent = ids.length > 1 ? 'Added ' + ids.length + ' ✓' : 'Added ✓';
      let opened = false;
      if (useDrawer) {
        try { closeDrawer(); cartDrawer.classList.remove('is-empty'); cartDrawer.renderContents(j); opened = true; } catch (e) {}
      }
      if (!opened) {
        updateCartUI(j.sections);
        toast({ title: ids.length > 1 ? ids.length + ' items added to cart' : 'Added to cart', action: 'View cart', onAction: () => { location.href = root + 'cart'; } });
      }
      setTimeout(() => { btn.innerHTML = label; btn.disabled = false; btn.classList.remove('is-done'); }, 2200);
    } catch (e) {
      btn.classList.remove('is-busy');
      btn.innerHTML = label;
      btn.disabled = false;
      toast({ title: 'Could not add to cart', text: e.message, error: true });
    }
  }

  // ---------------------------------------------------------------- share
  async function copyLink() {
    const url = shareUrl();
    try {
      if (navigator.share && matchMedia('(pointer: coarse)').matches) { await navigator.share({ title: 'My ' + CFG.shopName + ' wishlist', url }); return; }
      await navigator.clipboard.writeText(url);
      toast({ title: 'Link copied!', text: 'Paste it anywhere to share your wishlist' });
    } catch (e) {
      if (e && e.name === 'AbortError') return;
      prompt('Copy this link:', url);
    }
  }
  function whatsapp() {
    const msg = `Check out my wishlist at ${CFG.shopName} ❤️\n${shareUrl()}`;
    window.open('https://wa.me/?text=' + encodeURIComponent(msg), '_blank', 'noopener');
  }

  // ---------------------------------------------------------------- toast
  let toastEl, toastTimer;
  function toast({ img, title, text, action, onAction, error }) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'jtw-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
    }
    toastEl.classList.toggle('is-error', !!error);
    toastEl.innerHTML = `
      ${img ? `<img src="${esc(sized(img, 120))}" alt="" width="44" height="44">` : `<span class="jtw-toast__icon">${HEART}</span>`}
      <div class="jtw-toast__txt"><strong>${esc(title)}</strong>${text ? `<span>${esc(text)}</span>` : ''}</div>
      ${action ? `<button type="button" class="jtw-toast__act">${esc(action)}</button>` : ''}`;
    const b = $('.jtw-toast__act', toastEl);
    if (b) b.onclick = () => { hide(); onAction && onAction(); };
    clearTimeout(toastTimer);
    toastEl.classList.remove('is-show'); void toastEl.offsetWidth; toastEl.classList.add('is-show');
    const hide = () => toastEl.classList.remove('is-show');
    toastTimer = setTimeout(hide, action === 'Undo' ? 5000 : 3500);
  }

  // ---------------------------------------------------------------- optional cross-device sync (app proxy)
  let syncTimer;
  function queueSync() {
    if (!CFG.syncUrl || !CFG.customer) return;
    clearTimeout(syncTimer);
    syncTimer = setTimeout(() => {
      fetch(CFG.syncUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: list.map(({ h, v, t, at }) => ({ h, v, t, at })) }),
      }).catch(() => {});
    }, 1200);
  }
  async function pull() {
    if (!CFG.syncUrl || !CFG.customer || ss.get('jtw_pulled')) return;
    try {
      const r = await fetch(CFG.syncUrl, { headers: { Accept: 'application/json' } });
      if (!r.ok) return;
      const j = await r.json();
      ss.set('jtw_pulled', 1);
      let n = 0;
      (j.items || []).forEach((o) => { if (o && o.h && !list.some((i) => matches(i, o.h, o.v))) { list.push(o); n++; } });
      if (n) { commit({ silent: true }); refresh(); }
      queueSync();
    } catch (e) {}
  }

  // ---------------------------------------------------------------- events
  // Hearts are handled in the CAPTURE phase, before any theme/app click handler runs.
  // Some hover effects (e.g. Dawn "3D lift") put the card's product-link overlay above the
  // heart, so the click lands on the link. If the tap point is inside a heart's box we
  // still treat it as a heart tap and stop the link from opening the product page.
  function heartAt(e) {
    const direct = e.target.closest && e.target.closest('[data-jt-wish]');
    if (direct) return direct;
    if (!e.isTrusted || e.detail === 0) return null; // keyboard "clicks" have no position
    const card = e.target.closest && e.target.closest('.card-wrapper, product-component, .grid__item, .product-card, li, article');
    if (!card) return null;
    const x = e.clientX, y = e.clientY;
    for (const h of card.querySelectorAll('[data-jt-wish]')) {
      const r = h.getBoundingClientRect();
      if (r.width && x >= r.left - 4 && x <= r.right + 4 && y >= r.top - 4 && y <= r.bottom + 4) return h;
    }
    return null;
  }
  document.addEventListener('click', (e) => {
    const wish = heartAt(e);
    if (!wish) return;
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    toggle(wish);
  }, true);

  document.addEventListener('click', (e) => {
    const t = e.target;

    const opener = t.closest('[data-jt-wish-open]');
    if (opener && !(e.metaKey || e.ctrlKey || e.shiftKey)) {
      if (document.querySelector('[data-jt-wishlist-page]') && !shared) return; // already on the page – let link work
      e.preventDefault(); openDrawer(); return;
    }
    if (t.closest('[data-jtw-close]')) { e.preventDefault(); closeDrawer(); return; }

    const atc = t.closest('[data-jtw-atc]');
    if (atc) { e.preventDefault(); addToCart([atc.dataset.jtwAtc], atc); return; }

    const all = t.closest('[data-jtw-add-all]');
    if (all) { e.preventDefault(); addToCart(inStock().map((i) => i.vid || i.v), all); return; }

    const rm = t.closest('[data-jtw-rm]');
    if (rm) {
      e.preventDefault();
      const li = rm.closest('[data-h]');
      const h = li.dataset.h, v = li.dataset.v;
      const removed = list.filter((i) => i.h === h && String(i.v || '') === v);
      const go = () => {
        list = list.filter((i) => !(i.h === h && String(i.v || '') === v));
        commit();
        toast({ img: removed[0] && removed[0].img, title: 'Removed from wishlist', action: 'Undo', onAction: () => { list = removed.concat(list); commit(); } });
      };
      li.classList.add('is-leaving');
      setTimeout(go, 220);
      return;
    }

    if (t.closest('[data-jtw-wa]')) { e.preventDefault(); whatsapp(); return; }
    if (t.closest('[data-jtw-copy]')) { e.preventDefault(); copyLink(); return; }

    const clear = t.closest('[data-jtw-clear]');
    if (clear) {
      e.preventDefault();
      const backup = list.slice();
      list = []; commit();
      toast({ title: 'Wishlist cleared', action: 'Undo', onAction: () => { list = backup; commit(); } });
      return;
    }

    const save = t.closest('[data-jtw-save]');
    if (save) {
      e.preventDefault();
      const it = shared && shared.items.find((i) => i.h === save.dataset.jtwSave && String(i.v || '') === save.dataset.v);
      if (it && !list.some((i) => matches(i, it.h, it.v))) { list.unshift(Object.assign({}, it, { t: Date.now(), at: it.price })); commit(); }
      save.innerHTML = HEART + ' Saved'; save.disabled = true; save.classList.add('is-done');
      return;
    }
    if (t.closest('[data-jtw-save-all]')) {
      e.preventDefault();
      let n = 0;
      (shared ? shared.items : []).forEach((it) => { if (!list.some((i) => matches(i, it.h, it.v))) { list.unshift(Object.assign({}, it, { t: Date.now(), at: it.price })); n++; } });
      commit();
      toast({ title: n ? n + ' items saved to your wishlist' : 'Already in your wishlist', action: 'View', onAction: () => { location.href = CFG.pageUrl; } });
    }
  });

  document.addEventListener('change', (e) => {
    if (e.target.matches('[data-jtw-sort]')) { pageState.sort = e.target.value; renderPage(); return; }
    if (e.target.matches('[data-jtw-stock]')) { pageState.stock = e.target.checked; renderPage(); return; }
    if (e.target.closest('form[action*="/cart/add"], variant-selects, variant-radios')) setTimeout(paint, 60);
  });

  // ---------------------------------------------------------------- init
  function init() {
    paint();
    const page = $('[data-jt-wishlist-page]');
    if (page) {
      const sp = new URLSearchParams(location.search).get('share');
      if (sp) loadShared(sp);
      else { renderPage(); refresh(); pull(); }
    }
    if (location.hash === '#wishlist') openDrawer();
    if (pendingNotice) setTimeout(() => toast({ title: 'Wishlist saved to your account', text: pendingNotice }), 600);

    // New cards (filters, infinite scroll, quick view, recommendations) – batched to one frame
    let queued = false;
    new MutationObserver((muts) => {
      if (queued) return;
      if (drawer && muts.every((m) => drawer.contains(m.target))) return;
      queued = true;
      requestAnimationFrame(() => { queued = false; paint(); });
    }).observe(document.body, { childList: true, subtree: true });

    if (window.subscribe && window.PUB_SUB_EVENTS && PUB_SUB_EVENTS.variantChange) {
      try { subscribe(PUB_SUB_EVENTS.variantChange, () => setTimeout(paint, 60)); } catch (e) {}
    }
    window.addEventListener('storage', (e) => {
      if (e.key === KEY) { list = load(); paint(); renderAll(); }
    });
    window.addEventListener('pageshow', (e) => { if (e.persisted) { list = load(); paint(); } });
  }

  // Public API (handy for other theme scripts)
  window.JTWishlist = { open: openDrawer, close: closeDrawer, items: () => list.slice(), count: () => list.length };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
